/**
 * facilityProps.ts - Props specific to facility rooms
 */

export type FacilityPropId =
  | 'display_shelf'
  | 'cash_register'
  | 'coffee_machine'
  | 'menu_board'
  | 'camera_light'
  | 'backdrop'
  | 'hand_dryer'
  | 'mirror'
  | 'parking_sign'
  | 'barrier_gate'
  | 'first_aid_kit'
  | 'stretcher';

export interface FacilityPropDef {
  id: FacilityPropId;
  nameKey: string;
  descKey: string;
  maxLevel: number;
  baseCost: number;
  costMultiplier: number;
  /** Revenue or efficiency bonus per level */
  bonusPct: number;
  /** Which facility types this prop can be installed in */
  allowedFacilities: string[];
}

export const FACILITY_PROP_DEFS: Record<FacilityPropId, FacilityPropDef> = {
  display_shelf: {
    id: 'display_shelf',
    nameKey: 'fac_prop_display_shelf',
    descKey: 'fac_prop_display_shelf_desc',
    maxLevel: 3,
    baseCost: 120,
    costMultiplier: 2.0,
    bonusPct: 0.05,
    allowedFacilities: ['gift_shop'],
  },
  cash_register: {
    id: 'cash_register',
    nameKey: 'fac_prop_cash_register',
    descKey: 'fac_prop_cash_register_desc',
    maxLevel: 3,
    baseCost: 200,
    costMultiplier: 2.5,
    bonusPct: 0.08,
    allowedFacilities: ['gift_shop', 'snack_bar'],
  },
  coffee_machine: {
    id: 'coffee_machine',
    nameKey: 'fac_prop_coffee_machine',
    descKey: 'fac_prop_coffee_machine_desc',
    maxLevel: 3,
    baseCost: 180,
    costMultiplier: 2.0,
    bonusPct: 0.06,
    allowedFacilities: ['snack_bar'],
  },
  menu_board: {
    id: 'menu_board',
    nameKey: 'fac_prop_menu_board',
    descKey: 'fac_prop_menu_board_desc',
    maxLevel: 3,
    baseCost: 100,
    costMultiplier: 2.0,
    bonusPct: 0.04,
    allowedFacilities: ['snack_bar'],
  },
  camera_light: {
    id: 'camera_light',
    nameKey: 'fac_prop_camera_light',
    descKey: 'fac_prop_camera_light_desc',
    maxLevel: 3,
    baseCost: 250,
    costMultiplier: 2.5,
    bonusPct: 0.08,
    allowedFacilities: ['photo_booth'],
  },
  backdrop: {
    id: 'backdrop',
    nameKey: 'fac_prop_backdrop',
    descKey: 'fac_prop_backdrop_desc',
    maxLevel: 3,
    baseCost: 150,
    costMultiplier: 2.0,
    bonusPct: 0.06,
    allowedFacilities: ['photo_booth'],
  },
  hand_dryer: {
    id: 'hand_dryer',
    nameKey: 'fac_prop_hand_dryer',
    descKey: 'fac_prop_hand_dryer_desc',
    maxLevel: 3,
    baseCost: 80,
    costMultiplier: 2.0,
    bonusPct: 0.04,
    allowedFacilities: ['restroom'],
  },
  mirror: {
    id: 'mirror',
    nameKey: 'fac_prop_mirror',
    descKey: 'fac_prop_mirror_desc',
    maxLevel: 3,
    baseCost: 60,
    costMultiplier: 2.0,
    bonusPct: 0.03,
    allowedFacilities: ['restroom'],
  },
  parking_sign: {
    id: 'parking_sign',
    nameKey: 'fac_prop_parking_sign',
    descKey: 'fac_prop_parking_sign_desc',
    maxLevel: 3,
    baseCost: 100,
    costMultiplier: 2.0,
    bonusPct: 0.05,
    allowedFacilities: ['parking_lot'],
  },
  barrier_gate: {
    id: 'barrier_gate',
    nameKey: 'fac_prop_barrier_gate',
    descKey: 'fac_prop_barrier_gate_desc',
    maxLevel: 3,
    baseCost: 300,
    costMultiplier: 2.5,
    bonusPct: 0.08,
    allowedFacilities: ['parking_lot'],
  },
  first_aid_kit: {
    id: 'first_aid_kit',
    nameKey: 'fac_prop_first_aid_kit',
    descKey: 'fac_prop_first_aid_kit_desc',
    maxLevel: 3,
    baseCost: 150,
    costMultiplier: 2.0,
    bonusPct: 0.06,
    allowedFacilities: ['first_aid'],
  },
  stretcher: {
    id: 'stretcher',
    nameKey: 'fac_prop_stretcher',
    descKey: 'fac_prop_stretcher_desc',
    maxLevel: 3,
    baseCost: 200,
    costMultiplier: 2.5,
    bonusPct: 0.08,
    allowedFacilities: ['first_aid'],
  },
};

export const ALL_FACILITY_PROP_IDS = Object.keys(FACILITY_PROP_DEFS) as FacilityPropId[];

export function facilityPropCostForLevel(propId: FacilityPropId, level: number): number {
  const def = FACILITY_PROP_DEFS[propId];
  if (!def) return 0;
  return Math.floor(def.baseCost * Math.pow(def.costMultiplier, level - 1));
}

/** Get props available for a specific facility type */
export function getPropsForFacility(facilityId: string): FacilityPropId[] {
  return ALL_FACILITY_PROP_IDS.filter(
    (propId) => FACILITY_PROP_DEFS[propId].allowedFacilities.includes(facilityId),
  );
}
